import React from "react";
import { Box, Typography, Slider, Switch, CircularProgress, Tooltip } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import SendIcon from "@mui/icons-material/Send";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  TIKTOK_PRIVACY,
  TIKTOK_PRIVACY_LABELS,
  TIKTOK_PUBLISH_MODE,
} from "../constants";

const TIKTOK_COLOR = "#69C9D0";

const PRIVACY_ICONS = {
  [TIKTOK_PRIVACY.PUBLIC_TO_EVERYONE]: PublicIcon,
  [TIKTOK_PRIVACY.FOLLOWER_OF_CREATOR]: GroupIcon,
  [TIKTOK_PRIVACY.MUTUAL_FOLLOW_FRIENDS]: PersonIcon,
  [TIKTOK_PRIVACY.SELF_ONLY]: LockIcon,
};

const PUBLISH_MODES = [
  { value: TIKTOK_PUBLISH_MODE.DIRECT, label: "Direct", Icon: SendIcon },
  { value: TIKTOK_PUBLISH_MODE.INBOX, label: "Inbox", Icon: MoveToInboxIcon },
];

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        color: "var(--text-color)",
        opacity: 0.55,
        mb: "8px",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
      }}>
      {children}
    </Typography>
  );
}

function Hint({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "7px" }}>
      <InfoOutlinedIcon
        sx={{ fontSize: 13, color: "var(--text-color)", opacity: 0.35, mt: "2px", flexShrink: 0 }}
      />
      <Typography
        sx={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
}

function PillOption({ label, Icon, selected, onClick, disabled }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        padding: "6px 12px",
        borderRadius: "20px",
        cursor: disabled ? "not-allowed" : "pointer",
        border: selected
          ? `1px solid ${TIKTOK_COLOR}`
          : "1px solid var(--border-color)",
        backgroundColor: selected ? "rgba(105,201,208,0.12)" : "transparent",
        color: selected ? TIKTOK_COLOR : "var(--text-color)",
        opacity: disabled ? 0.3 : selected ? 1 : 0.55,
        fontSize: "12px",
        fontWeight: selected ? 600 : 400,
        transition: "all 0.15s",
        userSelect: "none",
        "&:hover": disabled
          ? {}
          : { opacity: 1, borderColor: TIKTOK_COLOR, color: TIKTOK_COLOR },
      }}>
      {Icon && <Icon sx={{ fontSize: 13 }} />}
      {label}
    </Box>
  );
}

function ToggleRow({ label, checked, forced, onChange, hint }) {
  const row = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
      }}>
      <Typography sx={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.85 }}>
        {label}
      </Typography>
      <Switch
        checked={Boolean(checked)}
        disabled={forced}
        onChange={(e) => onChange(e.target.checked)}
        size="small"
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": { color: TIKTOK_COLOR },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: TIKTOK_COLOR,
          },
        }}
      />
    </Box>
  );
  if (!hint) return row;
  return (
    <Box>
      {row}
      <Box sx={{ mt: "4px" }}>
        <Hint>{hint}</Hint>
      </Box>
    </Box>
  );
}

const TikTokSettings = ({
  creatorInfo,
  creatorInfoLoading,
  creatorInfoError,
  onRefreshCreatorInfo,
  isPhotoCarousel,
  fileCount,
  values,
  onChange,
}) => {
  const {
    tiktokPrivacyLevel,
    tiktokPublishMode,
    tiktokDisableComment,
    tiktokDisableDuet,
    tiktokDisableStitch,
    tiktokVideoCoverTimestampMs,
    tiktokPhotoCoverIndex,
    tiktokAutoAddMusic,
    tiktokBrandContentToggle,
    tiktokBrandOrganicToggle,
  } = values;

  const privacyOptions = creatorInfo?.privacy_level_options || [];
  const commentForced = Boolean(creatorInfo?.comment_disabled);
  const duetForced = Boolean(creatorInfo?.duet_disabled);
  const stitchForced = Boolean(creatorInfo?.stitch_disabled);

  // When backend forces a toggle on, reflect that locally so submit sends true.
  React.useEffect(() => {
    if (commentForced && !tiktokDisableComment) onChange("tiktokDisableComment", true);
  }, [commentForced]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (duetForced && !tiktokDisableDuet) onChange("tiktokDisableDuet", true);
  }, [duetForced]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (stitchForced && !tiktokDisableStitch) onChange("tiktokDisableStitch", true);
  }, [stitchForced]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clamp a stale privacy_level if Creator Info narrows the allowed set.
  React.useEffect(() => {
    if (privacyOptions.length === 0) return;
    if (tiktokPrivacyLevel && !privacyOptions.includes(tiktokPrivacyLevel)) {
      onChange("tiktokPrivacyLevel", privacyOptions[0]);
    }
    if (!tiktokPrivacyLevel) {
      onChange("tiktokPrivacyLevel", privacyOptions[0]);
    }
  }, [privacyOptions.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clamp photo_cover_index if file count shrinks.
  React.useEffect(() => {
    if (isPhotoCarousel && tiktokPhotoCoverIndex >= fileCount) {
      onChange("tiktokPhotoCoverIndex", 0);
    }
  }, [fileCount, isPhotoCarousel]); // eslint-disable-line react-hooks/exhaustive-deps

  const isInbox = tiktokPublishMode === TIKTOK_PUBLISH_MODE.INBOX;
  const isBrandedContent = tiktokBrandContentToggle || tiktokBrandOrganicToggle;
  const publicToEveryoneWithBrandedContent =
    tiktokBrandContentToggle && tiktokPrivacyLevel === TIKTOK_PRIVACY.SELF_ONLY;

  return (
    <Box className="post-card post-card-sm">
      <Box className="post-card-header post-card-header-sm" sx={{ gap: "8px" }}>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "4px",
            background: `linear-gradient(135deg, ${TIKTOK_COLOR} 0%, #EE1D52 100%)`,
          }}
        />
        <Typography className="post-card-title">TikTok Settings</Typography>
      </Box>

      <Box sx={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {creatorInfoLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CircularProgress size={14} sx={{ color: TIKTOK_COLOR }} />
            <Typography sx={{ fontSize: "12px", opacity: 0.6 }}>
              Loading TikTok creator info…
            </Typography>
          </Box>
        )}

        {creatorInfoError && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              backgroundColor: "rgba(255,80,80,0.08)",
              borderRadius: "8px",
              padding: "10px 12px",
              border: "1px solid rgba(255,80,80,0.25)",
            }}>
            <WarningAmberIcon sx={{ color: "#ff5050", fontSize: 15, mt: "2px" }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.85, lineHeight: 1.5 }}>
                Could not load TikTok creator info: {creatorInfoError}
              </Typography>
              {onRefreshCreatorInfo && (
                <Typography
                  onClick={onRefreshCreatorInfo}
                  sx={{
                    fontSize: "12px",
                    color: TIKTOK_COLOR,
                    cursor: "pointer",
                    mt: "4px",
                    fontWeight: 600,
                    "&:hover": { textDecoration: "underline" },
                  }}>
                  Retry
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {creatorInfo && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 12px",
              backgroundColor: "rgba(105,201,208,0.07)",
              borderRadius: "8px",
              border: "1px solid rgba(105,201,208,0.18)",
            }}>
            {creatorInfo.creator_avatar_url ? (
              <img
                src={creatorInfo.creator_avatar_url}
                alt={creatorInfo.creator_username}
                style={{ width: 32, height: 32, borderRadius: "50%" }}
              />
            ) : (
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: TIKTOK_COLOR,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: 14,
                  fontWeight: 700,
                }}>
                {(creatorInfo.creator_nickname || "?").charAt(0).toUpperCase()}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, color: "var(--text-color)" }}
                noWrap>
                Posting as {creatorInfo.creator_nickname || creatorInfo.creator_username}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.55 }} noWrap>
                @{creatorInfo.creator_username}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Publish mode */}
        <Box>
          <SectionLabel>Publish mode</SectionLabel>
          <Box sx={{ display: "flex", gap: "8px" }}>
            {PUBLISH_MODES.map(({ value, label, Icon }) => (
              <PillOption
                key={value}
                label={label}
                Icon={Icon}
                selected={tiktokPublishMode === value}
                onClick={() => onChange("tiktokPublishMode", value)}
              />
            ))}
          </Box>
          <Box sx={{ mt: "6px" }}>
            <Hint>
              {isInbox
                ? "Video will be sent to your TikTok drafts. You'll need to publish it manually in the TikTok app."
                : "Publish directly to your TikTok feed."}
            </Hint>
          </Box>
        </Box>

        {/* Everything below only applies to direct mode (backend ignores it for inbox) */}
        {!isInbox && (
          <>
            {/* Privacy */}
            <Box>
              <SectionLabel>Who can view</SectionLabel>
              {privacyOptions.length === 0 && !creatorInfoLoading ? (
                <Typography sx={{ fontSize: "12px", opacity: 0.55 }}>
                  Publishing is not available for this account.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {privacyOptions.map((opt) => {
                    const Icon = PRIVACY_ICONS[opt] || PublicIcon;
                    const disabled =
                      tiktokBrandContentToggle && opt === TIKTOK_PRIVACY.SELF_ONLY;
                    const pill = (
                      <PillOption
                        key={opt}
                        label={TIKTOK_PRIVACY_LABELS[opt] || opt}
                        Icon={Icon}
                        selected={tiktokPrivacyLevel === opt}
                        onClick={() => onChange("tiktokPrivacyLevel", opt)}
                        disabled={disabled}
                      />
                    );
                    return disabled ? (
                      <Tooltip
                        key={opt}
                        title="Branded content cannot be private."
                        arrow>
                        <Box>{pill}</Box>
                      </Tooltip>
                    ) : (
                      pill
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Interaction toggles */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <SectionLabel>Allow users to</SectionLabel>
              <ToggleRow
                label="Disable comments"
                checked={tiktokDisableComment || commentForced}
                forced={commentForced}
                onChange={(v) => onChange("tiktokDisableComment", v)}
                hint={commentForced ? "Locked by your TikTok account settings." : null}
              />
              {!isPhotoCarousel && (
                <>
                  <ToggleRow
                    label="Disable duet"
                    checked={tiktokDisableDuet || duetForced}
                    forced={duetForced}
                    onChange={(v) => onChange("tiktokDisableDuet", v)}
                    hint={duetForced ? "Locked by your TikTok account settings." : null}
                  />
                  <ToggleRow
                    label="Disable stitch"
                    checked={tiktokDisableStitch || stitchForced}
                    forced={stitchForced}
                    onChange={(v) => onChange("tiktokDisableStitch", v)}
                    hint={stitchForced ? "Locked by your TikTok account settings." : null}
                  />
                </>
              )}
            </Box>

            {/* Video-specific: cover frame timestamp */}
            {!isPhotoCarousel && (
              <Box>
                <SectionLabel>Cover frame</SectionLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Slider
                    value={tiktokVideoCoverTimestampMs}
                    min={0}
                    max={30000}
                    step={100}
                    onChange={(_, v) => onChange("tiktokVideoCoverTimestampMs", v)}
                    sx={{ color: TIKTOK_COLOR, flex: 1 }}
                  />
                  <Typography sx={{ fontSize: "12px", opacity: 0.7, minWidth: "48px" }}>
                    {(tiktokVideoCoverTimestampMs / 1000).toFixed(1)}s
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Photo-specific: cover index + auto music */}
            {isPhotoCarousel && fileCount > 0 && (
              <>
                <Box>
                  <SectionLabel>Cover photo</SectionLabel>
                  <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {Array.from({ length: fileCount }).map((_, idx) => (
                      <PillOption
                        key={idx}
                        label={String(idx + 1)}
                        selected={tiktokPhotoCoverIndex === idx}
                        onClick={() => onChange("tiktokPhotoCoverIndex", idx)}
                      />
                    ))}
                  </Box>
                </Box>
                <ToggleRow
                  label="Auto-add music"
                  checked={tiktokAutoAddMusic}
                  onChange={(v) => onChange("tiktokAutoAddMusic", v)}
                />
              </>
            )}

            {/* Branded content */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <SectionLabel>Branded content</SectionLabel>
              <ToggleRow
                label="Your brand"
                checked={tiktokBrandOrganicToggle}
                onChange={(v) => onChange("tiktokBrandOrganicToggle", v)}
              />
              <ToggleRow
                label="Branded content (third party)"
                checked={tiktokBrandContentToggle}
                onChange={(v) => {
                  onChange("tiktokBrandContentToggle", v);
                  if (v && tiktokPrivacyLevel === TIKTOK_PRIVACY.SELF_ONLY) {
                    const firstPublic = privacyOptions.find(
                      (p) => p !== TIKTOK_PRIVACY.SELF_ONLY,
                    );
                    if (firstPublic) onChange("tiktokPrivacyLevel", firstPublic);
                  }
                }}
              />
              {isBrandedContent && (
                <Box
                  sx={{
                    fontSize: "11px",
                    color: "var(--text-color)",
                    opacity: 0.7,
                    lineHeight: 1.55,
                    backgroundColor: "rgba(255,180,0,0.07)",
                    borderRadius: "6px",
                    padding: "8px 10px",
                    border: "1px solid rgba(255,180,0,0.22)",
                  }}>
                  By posting, you agree to TikTok's{" "}
                  <a
                    href="https://www.tiktok.com/legal/page/global/bc-policy/en"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: TIKTOK_COLOR }}>
                    Branded Content Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: TIKTOK_COLOR }}>
                    Music Usage Confirmation
                  </a>
                  .
                </Box>
              )}
              {publicToEveryoneWithBrandedContent && (
                <Hint>Branded content cannot be posted with "Only me" privacy.</Hint>
              )}
            </Box>

            {/* Video duration hint from creator info */}
            {creatorInfo?.max_video_post_duration_sec && !isPhotoCarousel && (
              <Hint>
                Max video length for this account:{" "}
                {Math.round(creatorInfo.max_video_post_duration_sec / 60)} min.
              </Hint>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TikTokSettings;
